import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { tierIncludes, useSubscriptionStore, type SubscriptionTier } from '@/stores/subscription'
import { TierBadge } from './TierBadge'
import { BetaBanner } from './BetaBanner'
import { Spinner } from './Spinner'
import { trackEvent } from '@/lib/analytics'

const TIER_NAME: Record<SubscriptionTier, string> = {
  basis: 'Chargee Basis',
  standaard: 'Chargee Standaard',
  premium: 'Chargee Premium',
}

/**
 * Wraps a service that requires a subscription tier: renders it when the
 * user's tier includes it, otherwise shows an upgrade pitch. easyEnergy pays
 * the subscription for its customers, so normally everyone is entitled and
 * sees the "included with easyEnergy" banner instead.
 */
export function PremiumGate({
  tier = 'premium',
  serviceId,
  children,
}: {
  tier?: SubscriptionTier
  /** Stable id for analytics (e.g. battery_advice). */
  serviceId?: string
  children: React.ReactNode
}) {
  const subscription = useSubscriptionStore((s) => s.subscription)
  const loading = useSubscriptionStore((s) => s.loading)
  const load = useSubscriptionStore((s) => s.load)
  const gateViewedRef = useRef(false)

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!subscription || tierIncludes(subscription.tier, tier)) return
    if (gateViewedRef.current) return
    gateViewedRef.current = true
    trackEvent('premium_gate_view', {
      service_id: serviceId,
      required_tier: tier,
      user_tier: subscription.tier,
      subscription_status: subscription.status,
    })
  }, [subscription, tier, serviceId])

  if (!subscription) {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-24">
          <Spinner />
        </div>
      )
    }
    return null
  }

  if (!tierIncludes(subscription.tier, tier)) {
    return (
      <div className="card-beige mx-auto max-w-xl p-6 text-center md:p-[30px]">
        <TierBadge tier={tier} />
        <h2 className="mt-4 text-xl lg:text-26">Deze dienst hoort bij {TIER_NAME[tier]}</h2>
        <p className="mt-2 text-base leading-normal text-dark-blue/60">
          Als klant van easyEnergy is dit abonnement inbegrepen — easyEnergy betaalt het voor
          je. Zien we je abonnement nog niet? Neem dan contact op met easyEnergy.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <a
            href="https://www.easyenergy.com/contact"
            className="btn-purple"
            onClick={() =>
              trackEvent('premium_upgrade_click', {
                service_id: serviceId,
                required_tier: tier,
                user_tier: subscription.tier,
              })
            }
          >
            Neem contact op
          </a>
          <Link to="/services" className="btn-secondary">
            Terug naar diensten
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {subscription.status === 'included' && <BetaBanner />}
      {children}
    </div>
  )
}
